// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// AddressAutocomplete — Google Places API address prediction
// Falls back to postal code lookup if no Google API key configured
// ================================================================
import React, { useState, useRef, useEffect, useCallback } from 'react';

const GOOGLE_MAPS_SCRIPT_ID = 'mt-google-maps-script';

// Load Google Maps JS API once globally
function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { resolve(); return; }
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

// Parse Google Place result into structured address
function parsePlace(place) {
  const components = {};
  (place.address_components || []).forEach((c) => {
    c.types.forEach((t) => { components[t] = c; });
  });

  return {
    street: [
      components.street_number?.long_name || '',
      components.route?.long_name || '',
    ].filter(Boolean).join(' '),
    city: components.locality?.long_name
      || components.sublocality_level_1?.long_name
      || components.administrative_area_level_3?.long_name
      || '',
    province: components.administrative_area_level_1?.short_name || '',
    postal: components.postal_code?.long_name || '',
    country: components.country?.short_name || '',
    lat: place.geometry?.location?.lat() || null,
    lng: place.geometry?.location?.lng() || null,
    formatted: place.formatted_address || '',
  };
}

/**
 * AddressAutocomplete
 *
 * Props:
 *   value       — current street address string
 *   onChange     — (streetValue) => void  (for the text input)
 *   onSelect    — ({ street, city, province, postal, country, lat, lng, formatted }) => void
 *   placeholder — input placeholder
 *   className   — CSS class for input
 *   disabled    — disable input
 *   apiKey      — Google Maps API key (reads REACT_APP_GOOGLE_MAPS_API_KEY env if not provided)
 *   country     — restrict to country codes ['ca', 'us'] (default both)
 *   id          — input element id
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  className = 'input',
  disabled = false,
  apiKey,
  country = ['ca', 'us'],
  id,
  style,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [fallbackSuggestions, setFallbackSuggestions] = useState([]);
  const [showFallback, setShowFallback] = useState(false);
  const fallbackTimerRef = useRef(null);

  const resolvedKey = apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // Initialize Google Places autocomplete
  useEffect(() => {
    if (!resolvedKey || disabled) return;
    let cancelled = false;

    loadGoogleMapsScript(resolvedKey).then(() => {
      if (cancelled || !inputRef.current) return;
      setLoaded(true);

      const options = {
        types: ['address'],
        fields: ['address_components', 'geometry', 'formatted_address'],
      };
      if (country?.length) {
        options.componentRestrictions = { country: Array.isArray(country) ? country : [country] };
      }

      const ac = new window.google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current = ac;

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.address_components) return;
        const parsed = parsePlace(place);
        if (onChange) onChange(parsed.street);
        if (onSelect) onSelect(parsed);
      });
    }).catch(() => {
      // Google Maps failed to load — fallback mode active
      setLoaded(false);
    });

    return () => {
      cancelled = true;
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [resolvedKey, disabled]);

  // Fallback: Nominatim (OpenStreetMap) free geocoding when no Google key
  const fetchFallbackSuggestions = useCallback(async (query) => {
    if (!query || query.length < 4) { setFallbackSuggestions([]); setShowFallback(false); return; }
    try {
      const countryCodes = Array.isArray(country) ? country.join(',') : (country || 'ca,us');
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=${countryCodes}&q=${encodeURIComponent(query)}`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const suggestions = data.map((item) => ({
        display: item.display_name,
        street: [item.address?.house_number || '', item.address?.road || ''].filter(Boolean).join(' '),
        city: item.address?.city || item.address?.town || item.address?.village || '',
        province: item.address?.state || item.address?.province || '',
        postal: item.address?.postcode || '',
        country: (item.address?.country_code || '').toUpperCase(),
        lat: parseFloat(item.lat) || null,
        lng: parseFloat(item.lon) || null,
        formatted: item.display_name,
      }));
      setFallbackSuggestions(suggestions);
      setShowFallback(suggestions.length > 0);
    } catch { /* silent */ }
  }, [country]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (onChange) onChange(val);

    // Only use fallback if Google isn't loaded
    if (!loaded && !resolvedKey) {
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = setTimeout(() => fetchFallbackSuggestions(val), 350);
    }
  };

  const handleFallbackSelect = (suggestion) => {
    if (onChange) onChange(suggestion.street);
    if (onSelect) onSelect(suggestion);
    setShowFallback(false);
    setFallbackSuggestions([]);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className={className}
        value={value}
        onChange={handleInputChange}
        onBlur={() => setTimeout(() => setShowFallback(false), 200)}
        onFocus={() => { if (fallbackSuggestions.length > 0) setShowFallback(true); }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {/* Fallback dropdown (only shows when Google Places is NOT available) */}
      {showFallback && !loaded && (
        <div className="address-autocomplete-dropdown">
          {fallbackSuggestions.map((s, i) => (
            <div
              key={i}
              className="address-autocomplete-item"
              onMouseDown={() => handleFallbackSelect(s)}
            >
              <span className="address-autocomplete-icon">📍</span>
              <div>
                <div className="address-autocomplete-main">{s.street || s.city}</div>
                <div className="address-autocomplete-sub">
                  {[s.city, s.province, s.postal].filter(Boolean).join(', ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
